/**
 * Isomorphic i18n dictionary core — no React, no Node, no Shopify imports (safe on both the
 * server and the browser bundle, same rule as label-form.ts).
 *
 * `en.json` is the canonical shape: every key that exists in the English dictionary is expected
 * to exist in every other locale's dictionary too. `translate()` falls back key-by-key to English
 * when a translation is missing (never blank UI), and falls back to the English dictionary
 * entirely when an unsupported/unknown locale is requested.
 *
 * Add a new locale by: adding its tag to SUPPORTED_LOCALES, adding `locales/<tag>.json` with the
 * same keys as en.json, and registering it in DICTIONARIES below.
 */
import en from "./locales/en.json";
import it from "./locales/it.json";

export const SUPPORTED_LOCALES = ["en", "it"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "en";

/** en.json is the canonical shape every other locale file must match. */
export type Dictionary = typeof en;

// `it` is authored by hand to mirror en.json's keys exactly; cast rather than fought over JSON
// import literal-widening so a translator adding a *new* English key doesn't get a spurious
// structural type error on the Italian file mid-edit — translate()'s runtime fallback (below)
// is what actually guarantees a missing Italian string never renders blank.
const DICTIONARIES: Record<Locale, Dictionary> = {
  en,
  it: it as Dictionary,
};

/**
 * Normalize a raw language tag (IETF BCP 47, e.g. "it-IT", "en_US", "fr") down to a locale we
 * support, defaulting to English for anything we don't ship a translation for.
 */
export function resolveLocale(candidate: string | null | undefined): Locale {
  if (!candidate) return DEFAULT_LOCALE;
  const parts = candidate.trim().toLowerCase().split(/[-_]/);
  const base = parts[0] ?? "";
  return (SUPPORTED_LOCALES as readonly string[]).includes(base) ? (base as Locale) : DEFAULT_LOCALE;
}

/** The full IETF tag used for locale-aware formatting (Intl / toLocaleDateString). */
export function localeToIntlTag(locale: Locale): string {
  return locale === "it" ? "it-IT" : "en-US";
}

function lookup(dict: Dictionary, key: string): string | undefined {
  const parts = key.split(".");
  let node: unknown = dict;
  for (const part of parts) {
    if (node == null || typeof node !== "object") return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return typeof node === "string" ? node : undefined;
}

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (match, name: string) => {
    const value = params[name];
    return value != null ? String(value) : match;
  });
}

/**
 * Look up `key` (dotted path, e.g. "dashboard.editButton") in `locale`'s dictionary, falling back
 * to English if the key is missing there, and to the raw key itself (never throwing / never
 * rendering blank) if it's missing from English too — a missed translation surfaces as a visible
 * key string during development instead of silently vanishing.
 */
export function translate(locale: Locale, key: string, params?: Record<string, string | number>): string {
  const dict = DICTIONARIES[locale] ?? DICTIONARIES[DEFAULT_LOCALE];
  const raw = lookup(dict, key) ?? lookup(DICTIONARIES[DEFAULT_LOCALE], key) ?? key;
  return interpolate(raw, params);
}
